import React, { useState, useEffect } from 'react';
import LayoutWrapper from '@iso/components/utility/layoutWrapper.js';
import Box from '@iso/components/utility/box';
import PageHeader from '@iso/components/utility/pageHeader';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Menu, Popconfirm, message, Tag, Alert, Result } from 'antd';
import { EditOutlined, PlusOutlined, DeleteOutlined, DownOutlined } from '@ant-design/icons';
import Table from '@iso/components/collapsing-table/table';
import moment from 'moment';
import { corporateGroupService } from '@iso/services';
import ManageCorporateGroupPage from './ManageCorporateGroup.styles';
import CorporateGroupModal from './CorporateGroupModal';

const ManageCorporateGroup = () => {
  const currentUser = useSelector((state) => state.user.user);
  const isAdmin = currentUser && currentUser.is_system_admin == 1;
  const [corporateGroups, setCorporateGroups] = useState([]);
  const [visibleModal, setVisibleModal] = useState(false);
  const [activeData, setActiveData] = useState({});

  const fetchGroups = () => {
    corporateGroupService.getList().then((res) => {
      if (res && res.code === '0000') {
        setCorporateGroups(res.corporate_groups || []);
      }
    });
  };

  useEffect(() => {
    if (isAdmin) {
      fetchGroups();
    }
  }, [isAdmin]);

  // Defense-in-depth: backend rejects non-admin mutations, but the route
  // itself is reachable by URL. Show an Access Denied screen rather than
  // letting the page render with the Add button visible.
  if (!isAdmin) {
    return (
      <LayoutWrapper>
        <Result
          status="403"
          title="Access denied"
          subTitle="Corporate Groups can only be managed by system administrators."
        />
      </LayoutWrapper>
    );
  }

  const showModal = (group = {}) => {
    setActiveData(group);
    setVisibleModal(true);
  };

  const cancelModal = () => {
    setVisibleModal(false);
    setActiveData({});
  };

  const confirmDelete = (group) => {
    if (!group.id) return;
    corporateGroupService.destroy(group.id).then((res) => {
      if (res && res.code === '0000') {
        message.success('Corporate group deleted');
        fetchGroups();
      } else {
        message.error(res?.message || 'Failed to delete corporate group');
      }
    });
  };

  const menu = (group) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => showModal(group)}>
        Edit
      </Menu.Item>
      <Menu.Item key="delete" danger>
        <Popconfirm
          placement="bottomRight"
          title="Delete this corporate group? Properties using it will be unassigned."
          onConfirm={() => confirmDelete(group)}
          okText="Yes"
          cancelText="No"
        >
          <span>
            <DeleteOutlined /> Delete
          </span>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      label: 'Name',
      accessor: 'name',
      minWidth: 100,
      sortable: true,
      position: 1,
      priorityLevel: 1,
    },
    {
      label: 'Description',
      accessor: 'description',
      minWidth: 100,
      sortable: false,
      position: 2,
      priorityLevel: 2,
    },
    {
      label: 'PIN',
      accessor: 'pin',
      minWidth: 80,
      sortable: false,
      position: 3,
      priorityLevel: 3,
      CustomComponent: (cell) => {
        return cell.row.pin
          ? <Tag color="green">{cell.row.pin}</Tag>
          : <Tag color="red">Not set</Tag>;
      },
    },
    {
      label: 'Properties',
      accessor: 'property_count',
      minWidth: 80,
      sortable: true,
      position: 4,
      priorityLevel: 4,
    },
    {
      label: 'Divisions',
      accessor: 'division_count',
      minWidth: 80,
      sortable: true,
      position: 5,
      priorityLevel: 5,
    },
    {
      label: 'Created',
      accessor: 'created_at',
      minWidth: 100,
      sortable: true,
      position: 6,
      priorityLevel: 6,
      CustomComponent: (cell) => {
        const v = cell.row.created_at;
        return v ? moment(v).format('L') : '';
      },
    },
    {
      label: '',
      accessor: 'actions',
      minWidth: 100,
      sortable: false,
      position: 7,
      priorityLevel: 7,
      noTitle: true,
      CustomComponent: (cell) => (
        <Dropdown overlay={menu(cell.row)} trigger={['click']}>
          <Button className="action-button">
            Actions <DownOutlined />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const hasDefaultPin = corporateGroups.some((g) => g.pin === '0000');

  return (
    <LayoutWrapper>
      <ManageCorporateGroupPage>
        <PageHeader>Corporate Groups</PageHeader>
        {hasDefaultPin && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Default PIN in use"
            description="Existing corporate groups were seeded with the default PIN '0000' during migration. Edit each group and set a unique 4-digit PIN before sharing it with property owners."
          />
        )}
        <div className="button-group">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            className="btn-success"
            onClick={() => showModal({})}
          >
            Add Corporate Group
          </Button>
        </div>
        <Box id="containerBox">
          {corporateGroups.length > 0 && (
            <Table
              columns={columns}
              rows={corporateGroups}
              containerId="containerBox"
              showPagination={false}
              rowSize={99999}
            />
          )}
        </Box>
        <CorporateGroupModal
          visible={visibleModal}
          corporateGroupData={activeData}
          onCancel={cancelModal}
          onSuccess={fetchGroups}
        />
      </ManageCorporateGroupPage>
    </LayoutWrapper>
  );
};

export default ManageCorporateGroup;
